// client/src/components/MiniCartDrawer.js
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer, Box, Typography, IconButton, Stack, Divider, Button, List, ListItem, ListItemAvatar,
  Avatar, ListItemText, LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { CartContext } from '../context/CartContext';
import { formatCurrency } from '../utils/currency';
import { getImageUrl } from '../utils/media';
import { FREE_SHIPPING_THRESHOLD } from '../constants/shipping';

const MiniCartDrawer = ({ open, onClose }) => {
  const { cartItems, removeFromCart, getCartTotal } = useContext(CartContext);
  const navigate = useNavigate();

  const goTo = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 320, sm: 380 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={700}>Your Cart ({cartItems.length})</Typography>
          <IconButton onClick={onClose} aria-label="Close cart"><CloseIcon /></IconButton>
        </Stack>
        <Divider />

        {cartItems.length > 0 && (() => {
          const subtotal = getCartTotal();
          const remaining = FREE_SHIPPING_THRESHOLD - subtotal;
          const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
          return (
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                <LocalShippingOutlinedIcon fontSize="small" color={remaining <= 0 ? 'success' : 'action'} />
                <Typography variant="caption" fontWeight={600}>
                  {remaining <= 0
                    ? "You've unlocked free shipping!"
                    : `Add ${formatCurrency(remaining)} more for free shipping`}
                </Typography>
              </Stack>
              <LinearProgress variant="determinate" value={progress} color={remaining <= 0 ? 'success' : 'primary'} sx={{ borderRadius: 4, height: 6 }} />
            </Box>
          );
        })()}

        {cartItems.length === 0 ? (
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
            <ShoppingCartOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
            <Typography color="text.secondary" sx={{ mb: 2 }}>Your cart is empty.</Typography>
            <Button variant="contained" onClick={() => goTo('/')}>Start Shopping</Button>
          </Box>
        ) : (
          <>
            <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {cartItems.map((item) => (
                <ListItem
                  key={item.cartKey}
                  secondaryAction={
                    <IconButton edge="end" size="small" color="error" onClick={() => removeFromCart(item.cartKey)} aria-label="Remove item">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" src={getImageUrl(item.imageUrl)} alt={item.name} sx={{ width: 56, height: 56, mr: 1 }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.name}
                    secondary={
                      <>
                        {item.variantLabel && `${item.variantLabel} · `}
                        Qty {item.quantity} · {formatCurrency(item.price * item.quantity)}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography fontWeight={700}>Subtotal</Typography>
                <Typography fontWeight={700}>{formatCurrency(getCartTotal())}</Typography>
              </Stack>
              <Stack spacing={1}>
                <Button variant="contained" fullWidth onClick={() => goTo('/cart')}>Checkout</Button>
                <Button variant="text" fullWidth onClick={() => goTo('/cart')}>View Cart</Button>
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default MiniCartDrawer;
